import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSettings } from "utils/settingsContext";
import { Icon, Text, HStack, VStack, Box } from "@chakra-ui/react";
import CountrySelector from "./CountrySelector";
import { IoIosAddCircleOutline } from "react-icons/io";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";

import { useOverlaySpinner } from "components/OverlaySpinner";
import { blockedCTAProps, useTOSGate } from "components/JustInTimeTOS";

/**
 * @param {Object} props
 * @param {Function} [props.onSelect]
 * @param {string} [props.countrySelectSize]
 * @param {boolean} [props.requireTOS=false] - opt in to just-in-time ToS gating. An opt-in prop
 * rather than gating unconditionally because this also renders on the payout side
 * (PayoutDestination), which is out of scope - the same reason AddPaypalButton and
 * AddPlaidACHButton take one.
 * @description The gate here is on the *disclosure*, not on the country selection: the selection is
 * already gated by the caller (AddPaymentMethodContent's handleBankCountrySelect, which signs and
 * then acts). Without this the row read as fully live next to a dimmed Add PayPal in the same
 * stack, and a blocked country pick answered only with a scroll that is a no-op when the consent
 * block is already in view.
 */
const AddBankAccountButton = ({ onSelect, countrySelectSize, requireTOS = false }) => {
  const { appSettings } = useSettings();
  const [showCountrySelector, setShowCountrySelector] = useState(false);
  const [checkingTOS, setCheckingTOS] = useState(false);
  const { blockedByTOS, ensureNotBlocked } = useTOSGate({ enabled: requireTOS });
  // `?? {}` rather than a bare destructure: OverlaySpinnerContext has no default value, and this
  // component renders under trees - the payout flow, and its own existing tests - that mount no
  // OverlaySpinnerProvider.
  const { showOverlaySpinner, hideOverlaySpinner } = useOverlaySpinner() ?? {};

  const supportedBankAccountCountries = appSettings.supported_bank_account_countries || {};

  // Consent can be withdrawn with the disclosure already open - tick, open the country list, untick
  // - and the gate on the pick itself is a handler guard, not a disabled control. Left open, the
  // list reads as live: a pick is answered by a scroll to a consent block that is already in view,
  // CountrySelector goes on showing the country it holds in its own state, and nothing says why
  // nothing happened. Collapsing puts the row back exactly as an un-consented user finds it.
  useEffect(() => {
    if (blockedByTOS) setShowCountrySelector(false);
  }, [blockedByTOS]);

  const handleCountrySelect = (countryOption) => {
    const countryISO = countryOption.value;
    const currency = countryOption.currency;
    const countryFormType = supportedBankAccountCountries[countryISO].add_bank_type || "masspay-form"; // default to masspay-form, only other current option is "plaid"

    onSelect &&
      onSelect({ name: countryOption.label, ISO: countryISO, type: countryFormType, currency });
  };

  const handleClick = async () => {
    // Collapsing is never gated, and an open gate awaits nothing - so the ungated path (every
    // caller that passes no `requireTOS`) stays the plain synchronous toggle it was.
    if (showCountrySelector || !blockedByTOS) {
      setShowCountrySelector(!showCountrySelector);
      return;
    }
    if (checkingTOS) return; // drop the second of two fast clicks

    // Up before the await: ensureNotBlocked may have to wait out the status fetch, and this row is
    // a styled div with a raw onClick - no disabled concept to show for it.
    setCheckingTOS(true);
    showOverlaySpinner?.();
    try {
      // A gate call rather than a `blockedByTOS` early return: the two reasons a click is blocked
      // need opposite answers - an unticked box can be scrolled to, an unresolved status has to be
      // waited out and re-decided - and only the gate knows which (see useTOSGate). No signTOS
      // here on purpose: opening a disclosure the user may back out of is not the act of adding a
      // payment method, so the sign stays at country selection.
      if (await ensureNotBlocked()) setShowCountrySelector(true);
    } finally {
      setCheckingTOS(false);
      hideOverlaySpinner?.();
    }
  };

  return (
    <VStack align="flex-start">
      <HStack
        spacing="1"
        color="integrator.400"
        fontWeight="600"
        cursor="pointer"
        {...blockedCTAProps(blockedByTOS)}
        onClick={handleClick}
      >
        <Icon as={IoIosAddCircleOutline} w="5" h="5" />
        <Text>
          <FormattedMessage defaultMessage="Add bank account" description="button label" />
        </Text>
        {showCountrySelector ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </HStack>
      <Box display={showCountrySelector ? "inline" : "none"}>
        {/* using a box instead of Collapse component as there are css issues using a Selector component within Collapse */}
        <VStack align="flex-start" gap="0">
          <Text fontSize="sm" color="secondaryText" pb="2">
            <FormattedMessage
              defaultMessage="Please select the country your bank account is in:"
              description="User message"
            />
          </Text>
          <CountrySelector size={countrySelectSize} onChange={handleCountrySelect} />
        </VStack>
      </Box>
    </VStack>
  );
};

export default AddBankAccountButton;
